import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import supabase from '../../supabase'
import { Helmet } from 'react-helmet-async'
import whiteicon from '../../assets/manifestSVG.svg'

export default function SignUp() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [lastname, setLastname] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [country, setCountry] = useState(null)
  const [agreed, setAgreed] = useState(false)

  useEffect(() => {
    fetch('https://ipapi.co/json/')
      .then(res => res.json())
      .then(data => setCountry(data.country_name))
      .catch(err => console.error('Geolocation error:', err))
  }, [])

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!agreed) {
      setErrorMsg('Please agree to the Terms & Conditions');
      return;
    }
    setLoading(true);
    setErrorMsg(''); 
    setSuccessMessage('');

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name, lastname, country: country || 'Unknown', isBusiness: false },
        },
      });

      if (error) throw error;
      if (data?.user && data.user.identities?.length === 0) {
        setErrorMsg('Email already exists. Please log in.');
        setLoading(false);
        return;
      }

      setSuccessMessage('Check your email to verify!');
      setTimeout(() => navigate('/signin'), 3000);
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen w-full bg-[#f4f4f4] flex items-center justify-center pb-[60px] px-4 py-6 perspective-1000'>
      <Helmet>
        <title>Register | Sitemanifest</title>
      </Helmet>

      <div className='w-full lg:max-w-[950px] mt-[60px] lg:mt-[90px] max-w-[450px] bg-white rounded-2xl shadow-[0_40px_80px_rgba(0,0,0,0.12)] border border-gray-100 overflow-hidden flex flex-col lg:flex-row lg:max-h-[640px] transform lg:rotate-y-[-4deg] lg:rotate-x-[2deg] transition-transform duration-700 hover:rotate-0'>
        
        {/* LEFT SIDE (Branding - Exactly as requested) */}
        <div className='hidden lg:flex lg:w-2/5 bg-[#1C6065] p-10 flex-col justify-between relative overflow-hidden'>
          <div className='absolute inset-0 z-0 opacity-[0.6] scale-150 transform translate-x-10 translate-y-10 select-none pointer-events-none'>
            <svg width="100%" height="100%" viewBox="0 0 800 800" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="400" cy="400" r="380" stroke="#ffffff" strokeWidth="1"/>
              <ellipse cx="400" cy="400" rx="380" ry="140" stroke="#ffffff" strokeWidth="1"/>
              <ellipse cx="400" cy="400" rx="140" ry="380" stroke="#ffffff" strokeWidth="1"/>
              <line x1="20" y1="400" x2="780" y2="400" stroke="#ffffff" strokeWidth="1"/>
              <line x1="400" y1="20" x2="400" y2="780" stroke="#ffffff" strokeWidth="1"/>
            </svg>
          </div>
          <div className='relative z-10'>
            <div className='flex flex-row items-center gap-[14px]'>
              <img src={whiteicon} alt="" className="w-7 h-7 object-contain brightness-0 invert" />
              <h2 className='font-staat text-white text-2xl tracking-widest uppercase'>SITEMANIFEST</h2>
            </div>
            <div className='w-8 h-[2px] bg-[#7CD998] mt-1'></div>
          </div>
          <div className='relative z-10 bg-white/10 backdrop-blur-md border border-white/10 p-6 rounded-xl shadow-xl'>
            <p className='text-white font-poppins text-sm italic opacity-90'>"Finally a platform where trust is the core feature."</p>
            <div className='mt-4 flex items-center gap-3 border-t border-white/10 pt-3 text-white text-xs font-semibold'>
              <div className='w-8 h-8 rounded-full bg-gradient-to-tr from-[#7CD998] to-white flex items-center justify-center text-[#1C6065]'>M</div>
              <p>Marcus V. <span className='text-[#7CD998] block text-[9px] uppercase font-bold'>Verified User</span></p>
            </div>
          </div>
          <div className='w-8 h-1 rounded-full bg-white relative z-10'></div>
        </div>

        {/* RIGHT SIDE (The Form) */}
        <div className='w-full lg:w-3/5 p-6 sm:p-10 flex flex-col justify-center bg-white relative'>
          
          <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4'>
            <div>
              <h1 className='text-2xl font-bold text-[#1C6065] tracking-tight'>Register</h1>
              <p className='text-gray-400 text-[11px] mt-1'>
                Already a member? <button onClick={() => navigate('/signin')} className='text-[#1E7D6B] font-bold hover:underline'>Log in</button>
              </p>
            </div>
            
            <div className='flex bg-gray-100 p-1 rounded-full self-start sm:self-center'>
              <button onClick={() => navigate('/signup')} className='px-4 py-1.5 text-[9px] font-bold rounded-full bg-white text-[#1C6065] shadow-sm'>PERSONAL</button>
              <button onClick={() => navigate('/business/signup')} className='px-4 py-1.5 text-[9px] font-bold rounded-full text-gray-400 hover:text-[#1C6065]'>BUSINESS</button>
            </div>
          </div>

          {/* MESSAGE AREA: Zero Height when empty, appears only on state change */}
          {(errorMsg || successMessage) && (
            <div className="mb-4 animate-message">
              {errorMsg && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-[11px] font-bold">
                  <span className="w-4 h-4 shrink-0 rounded-full bg-red-600 text-white flex items-center justify-center text-[10px]">!</span>
                  {errorMsg}
                </div>
              )}
              {successMessage && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-green-50 border border-green-100 text-[#1C6065] text-[11px] font-bold">
                  <span className="w-4 h-4 shrink-0 rounded-full bg-[#1C6065] text-white flex items-center justify-center text-[10px]">✓</span>
                  {successMessage}
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSignUp} className='space-y-3.5'>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3.5'>
              <div className='space-y-1'>
                <label className='text-[9px] uppercase tracking-widest font-bold text-gray-400 ml-1'>First Name</label>
                <input type="text" placeholder="First" value={name} onChange={(e) => setName(e.target.value)} required className="w-full bg-gray-50 border border-gray-100 text-sm h-[42px] rounded-xl px-4 outline-none focus:bg-white focus:border-[#1E7D6B] transition-all" />
              </div>
              <div className='space-y-1'>
                <label className='text-[9px] uppercase tracking-widest font-bold text-gray-400 ml-1'>Last Name</label>
                <input type="text" placeholder="Last" value={lastname} onChange={(e) => setLastname(e.target.value)} required className="w-full bg-gray-50 border border-gray-100 text-sm h-[42px] rounded-xl px-4 outline-none focus:bg-white focus:border-[#1E7D6B] transition-all" />
              </div>
            </div>

            <div className='space-y-1'>
              <label className='text-[9px] uppercase tracking-widest font-bold text-gray-400 ml-1'>Email</label>
              <input type="email" placeholder="email@address.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full bg-gray-50 border border-gray-100 text-sm h-[42px] rounded-xl px-4 outline-none focus:bg-white focus:border-[#1E7D6B] transition-all" />
            </div>

            <div className='space-y-1'>
              <label className='text-[9px] uppercase tracking-widest font-bold text-gray-400 ml-1'>Password</label>
              <input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full bg-gray-50 border border-gray-100 text-sm h-[42px] rounded-xl px-4 outline-none focus:bg-white focus:border-[#1E7D6B] transition-all" />
            </div>

            <div className='flex items-center gap-3 py-1 cursor-pointer select-none' onClick={() => setAgreed(!agreed)}>
              <div className={`w-5 h-5 shrink-0 rounded border-2 flex items-center justify-center transition-all ${agreed ? 'bg-[#1E7D6B] border-[#1E7D6B]' : 'border-gray-200'}`}>
                {agreed && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="4"><path d="M5 13l4 4L19 7"></path></svg>}
              </div>
              <p className='text-[10px] text-gray-500'>Accept <span className='text-[#1C6065] font-bold underline'>Terms</span></p>
            </div>

            <button type="submit" disabled={loading} className="w-full bg-[#1C6065] h-[46px] rounded-xl font-bold text-sm text-white shadow-lg hover:bg-[#164d51] transition-all transform active:scale-[0.98] disabled:opacity-50">
              {loading ? 'PROCESSING...' : 'REGISTER'}
            </button>

            <div className='flex items-center gap-4 py-1'>
              <div className='h-[1px] flex-1 bg-gray-100'></div>
              <span className='text-[9px] font-bold text-gray-300'>OR</span>
              <div className='h-[1px] flex-1 bg-gray-100'></div>
            </div>
<button 
  type="button" 
  className="w-full flex items-center justify-center gap-3 bg-white border border-gray-200 h-[46px] rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-50 transition-all active:scale-[0.98]"
>
  <div className="w-[18px] h-[18px] flex items-center justify-center overflow-visible">
    <svg 
      className="w-full h-full p-[0.5px]" 
      viewBox="0 0 48 48" 
      xmlns="http://www.w3.org/2000/svg"
    >
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
  </div>
  Google
</button>
          </form>
        </div>
      </div>
      
      <style dangerouslySetInnerHTML={{ __html: `
        .perspective-1000 { perspective: 1200px; }
        .rotate-y-[-4deg] { transform: rotateY(-4deg) rotateX(2deg); }
        .animate-message { animation: slideIn 0.3s ease-out forwards; }
        @keyframes slideIn { 
          from { opacity: 0; transform: translateY(-10px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
      `}} />
    </div>
  )
}